<template>
  <el-config-provider :locale="zhCn">
    <div class="app-container" :data-theme="themeStore.currentTheme">
      <Header v-if="!isLoginPage" />
      <div class="main-layout" :class="{ 'no-header': isLoginPage }">
        <router-view />
      </div>

      <AgreementLivenessQrDialog
        v-model="livenessQr.visible"
        :template-id="flow.templateId"
        :verify-mode="flow.verifyMode"
        @passed="onLivenessQrPassed"
        @cancel="onLivenessQrCancel"
      />
      <AgreementLivenessDialog
        v-model="livenessDialog.visible"
        :template-id="flow.templateId"
        :real-name="flow.realName"
        :id-card="flow.idCard"
        :verify-mode="flow.verifyMode"
        @passed="onLivenessMobilePassed"
      />
      <AgreementSignDialog
        v-model="signDialog.visible"
        :template-id="flow.templateId"
        :template-name="flow.templateName"
        :template-body="flow.templateBody"
        :certify-id="flow.certifyId"
        :ticket-id="flow.ticketId"
        @signed="onAgreementSigned"
      />
    </div>
  </el-config-provider>
</template>

<script lang="ts" setup>
import { ElConfigProvider, ElMessage } from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { useThemeStore } from './stores/themeStore'
import { onMounted, watch, computed, defineAsyncComponent, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { isUserLoggedIn } from '@/utils/auth'
import { checkAgreementSign, queryAgreementLiveness } from '@/api/agreement'
import { getSysConf } from '@/api/system'
import { getSelfApplication } from '@/api/profiles'
import { getUserInfoFromSessionStorage } from '@/utils/storage'
import { shouldOpenProofreadDialog } from '@/stores/proofreadingStore'
import AgreementSignDialog from '@/components/AgreementSignDialog.vue'
import AgreementLivenessDialog from '@/components/AgreementLivenessDialog.vue'
import AgreementLivenessQrDialog from '@/components/AgreementLivenessQrDialog.vue'
import { isMobileDevice } from '@/utils/agreementLiveness'
import {
  setAgreementGateBlocking,
  onAfterGatesReleased,
} from '@/utils/agreementGate'
import '@/utils/unpaidFeeDialog'
import { isDegreeOrThesisRoute } from '@/utils/unpaidFeeDialog'
import {
  confirmBeforeOpenNoticeSign,
  registerOpenAgreementGates,
  isNoticeSignPromptShowing,
} from '@/utils/noticeSignPrompt'

const Header = defineAsyncComponent(() => import('@/layouts/Header.vue'))

const themeStore = useThemeStore()
const route = useRoute()
const router = useRouter()

const isLoginPage = computed(() => {
  const p = route.path || ''
  return (
    p === '/login' ||
    p === '/register' ||
    p === '/forgot-password' ||
    p === '/exercises/mobile-monitor' ||
    p === '/agreement-liveness' ||
    p.startsWith('/agreement-liveness') ||
    !!route.meta?.blankLayout
  )
})

/**
 * 知情同意 开
 *   ├─ 已签署 → 进系统（不活体）
 *   └─ 未签署
 *         ├─ 活体开 → 先活体 → 再手签
 *         └─ 活体关 → 只手签
 * 知情同意 关
 *   ├─ 活体开 → 弹活体（不手签，不看是否已签署）
 *   └─ 活体关 → 进系统
 * 之后再弹缴费（1003）：活体 → 手签 → 缴费
 */
const flow = reactive({
  templateId: '' as string | number,
  templateName: '',
  templateBody: '',
  certifyId: '',
  ticketId: '',
  realName: '',
  idCard: '',
  verifyMode: '',
  needSignAfterLiveness: false,
  checked: false,
})

const livenessQr = reactive({ visible: false })
const livenessDialog = reactive({ visible: false })
const signDialog = reactive({ visible: false })

const openLiveness = () => {
  setAgreementGateBlocking(true)
  if (isMobileDevice()) {
    livenessDialog.visible = true
  } else {
    livenessQr.visible = true
  }
}

const openSign = () => {
  setAgreementGateBlocking(true)
  signDialog.visible = true
}

const afterLivenessPassed = () => {
  livenessQr.visible = false
  livenessDialog.visible = false
  if (flow.needSignAfterLiveness) {
    openSign()
  } else {
    // 仅活体、无需手签 → 闸门结束，再弹缴费
    setAgreementGateBlocking(false)
  }
}

const onLivenessQrPassed = (payload: { certifyId: string; ticketId: string }) => {
  flow.certifyId = payload.certifyId
  flow.ticketId = payload.ticketId
  afterLivenessPassed()
}

const onLivenessQrCancel = () => {
  releaseGates()
}

const onLivenessMobilePassed = (payload: { certifyId: string }) => {
  flow.certifyId = payload.certifyId
  flow.ticketId = ''
  afterLivenessPassed()
}

const fillTemplateFromCheck = (res: any) => {
  flow.templateId = res?.templateId || ''
  flow.templateName = res?.templateName || '协议签署'
  flow.templateBody = res?.templateBody || ''
  flow.realName = res?.realName || ''
  flow.idCard = res?.idCardFull || ''
  flow.verifyMode = res?.verifyMode || (res?.hasFaceBase ? 'viapi_liveness' : 'glow')
  flow.certifyId = ''
  flow.ticketId = ''
}

const releaseGates = () => {
  livenessQr.visible = false
  livenessDialog.visible = false
  signDialog.visible = false
  // 无活体/手签占用 → 释放并弹出暂存的缴费提示
  setAgreementGateBlocking(false)
}

/**
 * 告知函签署闸门：与 H5 一致，直接信后端 checkSign。
 * 默认先弹「去签署」确认，再打开活体二维码/手签；skipPrompt 用于用户已点「去签署」。
 * 二阶段告知函仅在学位申请页催签，课程等页面不弹。
 */
const ensureGates = async (opts?: { skipPrompt?: boolean }) => {
  if (!isUserLoggedIn() || isLoginPage.value) return
  // 已在催签/弹框/二维码中：勿重复进入，避免叠弹或误 release
  if (
    !opts?.skipPrompt &&
    (livenessQr.visible ||
      livenessDialog.visible ||
      signDialog.visible ||
      isNoticeSignPromptShowing())
  ) {
    return
  }
  // 先占用闸门，避免课程列表 1003 抢在活体/手签前弹出
  setAgreementGateBlocking(true)
  try {
    const res: any = await checkAgreementSign()
    const livenessOn = !!res?.livenessEnabled
    const livenessPassed = !!res?.livenessPassed
    const needSign = !!res?.needSign
    const hasTemplate = !!res?.templateId
    const noticePhase = Number(res?.noticePhase || 0)

    // 二阶段：非学位页且非「去签署」续流程 → 跳过，不拦课程
    if (noticePhase === 2 && !isDegreeOrThesisRoute() && !opts?.skipPrompt) {
      releaseGates()
      return
    }

    if (!livenessOn && !needSign) {
      releaseGates()
      return
    }

    const askThenOpen = async (openFn: () => void) => {
      if (opts?.skipPrompt) {
        openFn()
        return
      }
      const ok = await confirmBeforeOpenNoticeSign('', res)
      if (ok === true) {
        openFn()
      } else if (ok === false) {
        releaseGates()
      }
      // ok === null：已有「去签署」弹框进行中，勿 release，避免误关闸门
    }

    // ---------- 活体未过 ----------
    if (livenessOn && !livenessPassed) {
      if (!hasTemplate) {
        ElMessage.warning('已开启告知函活体，但暂无已发布的协议模板，无法发起认证')
        releaseGates()
        return
      }
      fillTemplateFromCheck(res)
      flow.needSignAfterLiveness = needSign

      const pendingRaw = sessionStorage.getItem('agreement_liveness_pending')
      if (pendingRaw && isMobileDevice()) {
        try {
          const pending = JSON.parse(pendingRaw)
          if (!pending.ticketId) {
            const q: any = await queryAgreementLiveness(pending.certifyId)
            if (q?.passed) {
              sessionStorage.removeItem('agreement_liveness_pending')
              flow.certifyId = pending.certifyId
              if (needSign) {
                openSign()
              } else {
                releaseGates()
              }
              return
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
      await askThenOpen(openLiveness)
      return
    }

    // ---------- 需签署 ----------
    if (needSign) {
      if (!hasTemplate) {
        ElMessage.warning('暂无已发布的告知函模板')
        releaseGates()
        return
      }
      fillTemplateFromCheck(res)
      flow.needSignAfterLiveness = false
      if (res?.certifyId) {
        flow.certifyId = res.certifyId
      }
      await askThenOpen(openSign)
      return
    }

    releaseGates()
  } catch (e) {
    console.error('登录闸门校验失败:', e)
    releaseGates()
  } finally {
    flow.checked = true
  }
}

registerOpenAgreementGates((opts?: { skipPrompt?: boolean }) => ensureGates(opts))

const onAgreementSigned = () => {
  signDialog.visible = false
  flow.certifyId = ''
  flow.ticketId = ''
  flow.needSignAfterLiveness = false
  // 手签完成 → 再弹缴费
  setAgreementGateBlocking(false)
}

// ========== 校对提醒检查 ==========
const proofreadingChecked = ref(false)
let proofreadingRetryTimer: ReturnType<typeof setInterval> | null = null

// 当用户从登录页跳转到其他页面时（即完成登录），重置校对检查标记
watch(isLoginPage, (isLogin, wasLogin) => {
  if (wasLogin && !isLogin && isUserLoggedIn()) {
    console.log('[校对提醒] 检测到登录完成，重置检查标记')
    proofreadingChecked.value = false
  }
})

const ensureProofreadingReminder = async () => {
  console.log('[校对提醒] 开始检查', {
    isLoggedIn: isUserLoggedIn(),
    isLoginPage: isLoginPage.value,
    proofreadingChecked: proofreadingChecked.value,
    currentPath: route.path
  })

  // 未登录或在登录页：不标记完成，等下次路由变化再检查
  if (!isUserLoggedIn() || isLoginPage.value) {
    console.log('[校对提醒] 未登录或在登录页，跳过（不标记完成）')
    return
  }

  // 已完成检查，不再重复
  if (proofreadingChecked.value) {
    console.log('[校对提醒] 已检查完成，跳过')
    return
  }

  try {
    // 1. 检查 proofreadingReminder 配置是否开启
    console.log('[校对提醒] 调用 getSysConf(proofreadingReminder)')
    const confRes: any = await getSysConf('proofreadingReminder')
    console.log('[校对提醒] getSysConf 返回:', confRes)
    const confStr = confRes?.conf ?? confRes?.data?.conf
    let enabled = false
    if (confStr) {
      const config = typeof confStr === 'string' ? JSON.parse(confStr) : confStr
      enabled = config?.enabled === true || config === true
    }
    console.log('[校对提醒] 配置 enabled:', enabled)

    if (!enabled) {
      proofreadingChecked.value = true
      return
    }

    // 2. 检查用户是否已有申请记录
    const userInfo = getUserInfoFromSessionStorage() as any
    const userId = userInfo?.userId
    console.log('[校对提醒] userId:', userId)
    if (!userId) {
      proofreadingChecked.value = true
      return
    }

    const appRes: any = await getSelfApplication(userId)
    console.log('[校对提醒] getSelfApplication 返回:', JSON.stringify(appRes))
    const appData = appRes?.data || appRes
    console.log('[校对提醒] appData:', JSON.stringify(appData), 'hasId:', !!appData?.id)

    // 3. 如果没有申请记录（没有 id），则触发校对弹窗
    if (!appData?.id) {
      console.log('[校对提醒] 无申请记录，触发校对弹窗')
      shouldOpenProofreadDialog.value = true

      if (route.path !== '/profile/personal') {
        console.log('[校对提醒] 跳转到 PersonalInfo 页面')
        router.push('/profile/personal')
      } else {
        console.log('[校对提醒] 已在 PersonalInfo 页面，设置响应式标记')
      }
    } else {
      console.log('[校对提醒] 已有申请记录，不弹框')
    }

    proofreadingChecked.value = true
  } catch (e: any) {
    console.log('[校对提醒] catch 触发, error:', JSON.stringify(e))
    const is1003 = e?.errorCode == '1003'
      || e?.response?.data?.errorCode == '1003'
      || e?.data?.errorCode == '1003'

    if (is1003) {
      console.log('校对提醒检查被欠费拦截，等待欠费弹框关闭后重试')
      proofreadingChecked.value = false
      if (!proofreadingRetryTimer) {
        proofreadingRetryTimer = setInterval(() => {
          const dialogStillShowing = (window as any).__isShowing1003Dialog
          if (!dialogStillShowing) {
            clearInterval(proofreadingRetryTimer!)
            proofreadingRetryTimer = null
            ensureProofreadingReminder()
          }
        }, 500)
      }
    } else {
      console.error('校对提醒检查失败:', e)
      proofreadingChecked.value = true
    }
  }
}

watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
    ensureGates()
    // 闸门释放 + 缴费弹框结束后再检查校对提醒（事件驱动，非固定延时）
    onAfterGatesReleased(() => {
      ensureProofreadingReminder()
    })
  },
  { immediate: true },
)

onMounted(() => {
  themeStore.initTheme()
})
</script>

<style lang="scss">
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background-color: var(--background-color);
  color: var(--text-color);
  transition: background-color 0.3s, color 0.3s;

  .main-layout {
    flex: 1;
    margin-top: 60px;

    &.no-header {
      margin-top: 0;
    }
  }
}
</style>
